/**
 * Domains — navigational grouping of assets, declared by `DOMAIN.md` files
 * supplied by a sidecar (never the upstream repo).
 *
 * Pure logic only — zero I/O. A `DOMAIN.md` claims the directory it lives in
 * plus everything below it (whole-segment containment). Domains are strictly
 * non-nested, so every asset has at most one claiming domain. The slug is
 * display-only metadata: it never participates in asset identity
 * (`<publisher>/<name>@<version>`) or the flat install layout.
 *
 * @module
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** How members of a domain relate to one another (navigational hint, never install-all). */
export type DomainRelation = "additions" | "alternatives" | "sequence";

const DOMAIN_RELATIONS: ReadonlySet<string> = new Set<DomainRelation>([
  "additions",
  "alternatives",
  "sequence",
]);

/** GitHub-shaped slug grammar: lowercase alphanumeric + single hyphens, ≤39 chars. */
const DOMAIN_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const DOMAIN_SLUG_MAX = 39;

/** A single member-overlay entry from a `DOMAIN.md` `members:` list. */
export interface DomainMember {
  /** Canonical or bare asset ref the entry refers to. */
  ref: string;
  /** Display order within the domain (required for `sequence`). */
  order?: number;
  /** Marks the default pick within an `alternatives` domain. Never auto-installed. */
  recommended?: boolean;
}

/**
 * A parsed `DOMAIN.md` descriptor. `domainRoot` is the repo-relative directory
 * the file lives in (its claimed subtree root); `''` means the repo root.
 */
export interface DomainMd {
  /** `dirname` of the DOMAIN.md path; `''` = repo root (claims the whole repo). */
  domainRoot: string;
  slug: string;
  title: string;
  description: string;
  relation: DomainRelation;
  order?: number;
  members?: DomainMember[];
}

/** A resolved domain plus its ordered member set, ready for display/persistence. */
export interface DomainIndexEntry {
  slug: string;
  title: string;
  description: string;
  relation: DomainRelation;
  order?: number;
  /** Asset refs in this domain, ordered by member `order` then discovery order. */
  members: string[];
  /** Asset refs flagged `recommended` by the member overlay. */
  recommended: string[];
}

/** Hard validation errors. Any of these fails the sidecar build (proposal §4). */
export type DomainValidationError =
  | { code: "DomainNesting"; outer: string; inner: string }
  | { code: "DuplicateDomainSlug"; slug: string; roots: string[] }
  | { code: "InvalidRelation"; root: string; value: string }
  | { code: "InvalidSlug"; root: string; value: string };

/** Minimal asset shape the domain resolver needs (a `DiscoveredAsset` satisfies it). */
export interface DomainAssignable {
  ref: string;
  relativePath: string;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/** The filename that marks a domain descriptor. */
export const DOMAIN_MD_FILENAME = "DOMAIN.md";

/** True when a repo-relative path is a `DOMAIN.md` descriptor file. */
export function isDomainMdPath(relativePath: string): boolean {
  return relativePath === DOMAIN_MD_FILENAME || relativePath.endsWith(`/${DOMAIN_MD_FILENAME}`);
}

/** `dirname` for a POSIX repo-relative path; `''` for a top-level file. */
function posixDirname(p: string): string {
  const i = p.lastIndexOf("/");
  return i === -1 ? "" : p.slice(0, i);
}

function normalizeMembers(raw: unknown): DomainMember[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: DomainMember[] = [];
  for (const m of raw) {
    if (typeof m === "string" && m.length > 0) {
      out.push({ ref: m });
    } else if (m && typeof m === "object" && typeof (m as { ref?: unknown }).ref === "string") {
      const obj = m as { ref: string; order?: unknown; recommended?: unknown };
      const member: DomainMember = { ref: obj.ref };
      if (typeof obj.order === "number") member.order = obj.order;
      if (obj.recommended === true) member.recommended = true;
      out.push(member);
    }
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Build a {@link DomainMd} from a `DOMAIN.md` path and its parsed frontmatter.
 *
 * Tolerant: missing/invalid fields are carried through as-is so
 * {@link validateDomains} can report them as hard errors rather than throwing.
 * `title` falls back to `slug`; `description` to the empty string;
 * `relation` to `additions`.
 *
 * @param domainMdPath - repo-relative path to the `DOMAIN.md` file
 * @param frontmatter - parsed YAML frontmatter object
 */
export function parseDomainMd(
  domainMdPath: string,
  frontmatter: Record<string, unknown>,
): DomainMd {
  const slug = typeof frontmatter.slug === "string" ? frontmatter.slug : "";
  const title =
    typeof frontmatter.title === "string" && frontmatter.title.length > 0
      ? frontmatter.title
      : slug;
  const description =
    typeof frontmatter.description === "string" ? frontmatter.description.trim() : "";
  const relation = typeof frontmatter.relation === "string" ? frontmatter.relation : "additions";
  const order = typeof frontmatter.order === "number" ? frontmatter.order : undefined;
  const members = normalizeMembers(frontmatter.members);

  return {
    domainRoot: posixDirname(domainMdPath),
    slug,
    title,
    description,
    relation: relation as DomainRelation,
    ...(order !== undefined ? { order } : {}),
    ...(members ? { members } : {}),
  };
}

// ---------------------------------------------------------------------------
// Containment helpers
// ---------------------------------------------------------------------------

/**
 * Whole-segment containment: `true` when `root` is an ancestor-or-self of
 * `target`. `''` (repo root) is an ancestor of everything. Avoids the
 * `skills` vs `skills-extra` false positive by requiring a `/` boundary.
 */
function claims(root: string, target: string): boolean {
  if (root === "") return true;
  return target === root || target.startsWith(`${root}/`);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validate a set of `DOMAIN.md` descriptors. Returns the list of hard errors;
 * an empty list means the set is valid. Never throws.
 *
 * Checks (proposal §4):
 *  - `InvalidSlug`   — slug missing or not GitHub-shaped (≤39, `[a-z0-9-]`).
 *  - `InvalidRelation` — relation not one of additions|alternatives|sequence.
 *  - `DuplicateDomainSlug` — same slug on ≥2 domains.
 *  - `DomainNesting` — one domain's root is inside another's claimed subtree.
 */
export function validateDomains(domains: readonly DomainMd[]): DomainValidationError[] {
  const errors: DomainValidationError[] = [];

  // Per-domain field validation.
  for (const d of domains) {
    if (!d.slug || !DOMAIN_SLUG_RE.test(d.slug) || d.slug.length > DOMAIN_SLUG_MAX) {
      errors.push({ code: "InvalidSlug", root: d.domainRoot, value: d.slug });
    }
    if (!DOMAIN_RELATIONS.has(d.relation)) {
      errors.push({ code: "InvalidRelation", root: d.domainRoot, value: d.relation });
    }
  }

  // Duplicate slug across domains.
  const bySlug = new Map<string, string[]>();
  for (const d of domains) {
    if (!d.slug) continue;
    const roots = bySlug.get(d.slug) ?? [];
    roots.push(d.domainRoot);
    bySlug.set(d.slug, roots);
  }
  for (const [slug, roots] of bySlug) {
    if (roots.length > 1) errors.push({ code: "DuplicateDomainSlug", slug, roots: roots.sort() });
  }

  // Nesting: sort roots, then for each ordered pair check whole-segment ancestry.
  // domainRoots are distinct (one DOMAIN.md per dir), so equality cannot occur.
  const roots = domains.map((d) => d.domainRoot).sort();
  for (let i = 0; i < roots.length; i++) {
    for (let j = i + 1; j < roots.length; j++) {
      const a = roots[i]!;
      const b = roots[j]!;
      if (a === b) continue;
      // After sort, a <= b lexically; either could still be the ancestor.
      if (claims(a, b)) errors.push({ code: "DomainNesting", outer: a, inner: b });
      else if (claims(b, a)) errors.push({ code: "DomainNesting", outer: b, inner: a });
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Assignment
// ---------------------------------------------------------------------------

/**
 * Assign an asset (by its repo-relative manifest path) to a domain slug, or
 * `null` when no domain claims it. Assumes {@link validateDomains} passed, so
 * at most one domain claims any path; when several match (defensive, e.g.
 * validation skipped) the deepest root wins.
 */
export function assignDomain(relativePath: string, domains: readonly DomainMd[]): string | null {
  let best: DomainMd | undefined;
  for (const d of domains) {
    if (!d.slug) continue; // invalid-slug domains are reported, not assigned
    if (!claims(d.domainRoot, relativePath)) continue;
    if (!best || d.domainRoot.length > best.domainRoot.length) best = d;
  }
  return best ? best.slug : null;
}

// ---------------------------------------------------------------------------
// Index
// ---------------------------------------------------------------------------

/**
 * Build the flat display index: every asset assigned to its domain, members
 * ordered by member `order` (asc) then discovery order, plus the `unclaimed`
 * list. Assets are consumed in their given order (callers pass discovery
 * order) so the output is deterministic.
 *
 * Member overlay semantics: a `members[]` entry only reorders / flags assets
 * already in the claimed subtree. A member ref outside the subtree is ignored
 * in v1 (warnings deferred).
 */
export function buildDomainIndex(
  assets: readonly DomainAssignable[],
  domains: readonly DomainMd[],
): { index: Record<string, DomainIndexEntry>; unclaimed: string[] } {
  const index: Record<string, DomainIndexEntry> = {};
  const unclaimed: string[] = [];

  const bySlug = new Map<string, DomainMd>();
  for (const d of domains) bySlug.set(d.slug, d);

  // Discovery-order list of refs per slug.
  const refsBySlug = new Map<string, string[]>();
  for (const asset of assets) {
    const slug = assignDomain(asset.relativePath, domains);
    if (slug === null) {
      unclaimed.push(asset.ref);
      continue;
    }
    const list = refsBySlug.get(slug) ?? [];
    list.push(asset.ref);
    refsBySlug.set(slug, list);
  }

  for (const [slug, refs] of refsBySlug) {
    const d = bySlug.get(slug)!;
    const memberOrder = new Map<string, number>();
    const recommended = new Set<string>();
    for (const m of d.members ?? []) {
      if (m.order !== undefined) memberOrder.set(m.ref, m.order);
      if (m.recommended) recommended.add(m.ref);
    }
    // Stable sort: members with an explicit order first (by order), then the
    // rest in discovery order. Preserve discovery order as the tiebreaker.
    const ordered = refs
      .map((ref, i) => ({ ref, i, order: memberOrder.get(ref) }))
      .sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) return a.order - b.order || a.i - b.i;
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;
        return a.i - b.i;
      })
      .map((x) => x.ref);

    index[slug] = {
      slug,
      title: d.title,
      description: d.description,
      relation: d.relation,
      ...(d.order !== undefined ? { order: d.order } : {}),
      members: ordered,
      recommended: ordered.filter((ref) => recommended.has(ref)),
    };
  }

  return { index, unclaimed };
}

// ---------------------------------------------------------------------------
// Discovery integration
// ---------------------------------------------------------------------------

/** Render a {@link DomainValidationError} as a `{ path, error }` discovery error. */
export function describeDomainError(e: DomainValidationError): { path: string; error: string } {
  switch (e.code) {
    case "DomainNesting":
      return {
        path: `${e.inner}/${DOMAIN_MD_FILENAME}`,
        error: `DomainNesting: domain at '${e.inner}' is nested inside domain at '${e.outer || "<repo root>"}'`,
      };
    case "DuplicateDomainSlug":
      return {
        path: `${e.roots[0] ?? ""}/${DOMAIN_MD_FILENAME}`,
        error: `DuplicateDomainSlug: slug '${e.slug}' declared by ${e.roots.join(", ")}`,
      };
    case "InvalidRelation":
      return {
        path: `${e.root}/${DOMAIN_MD_FILENAME}`,
        error: `InvalidRelation: '${e.value}' (expected additions|alternatives|sequence)`,
      };
    case "InvalidSlug":
      return {
        path: `${e.root}/${DOMAIN_MD_FILENAME}`,
        error: `InvalidSlug: '${e.value}' (expected lowercase a-z0-9 with single hyphens, ≤39 chars)`,
      };
  }
}

/**
 * Apply a set of parsed `DOMAIN.md` descriptors to a discovered asset set:
 * validate, stamp each asset's `domainSlug` in place, and return the ordered
 * domain index plus any hard validation errors (as discovery `{ path, error }`).
 *
 * Hard errors are reported but never abort the run — invalid domains are simply
 * not assigned (proposal §4: surface, don't crash). The returned `domains` are
 * sorted by `order` (asc) then slug for stable display.
 */
export function applyDomains(
  assets: Array<DomainAssignable & { domainSlug?: string }>,
  domains: readonly DomainMd[],
): { domains: DomainIndexEntry[]; errors: Array<{ path: string; error: string }> } {
  const errors = validateDomains(domains).map(describeDomainError);

  for (const a of assets) {
    const slug = assignDomain(a.relativePath, domains);
    if (slug) a.domainSlug = slug;
  }

  const { index } = buildDomainIndex(assets, domains);
  const ordered = Object.values(index).sort(
    (a, b) =>
      (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY) ||
      (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0),
  );

  return { domains: ordered, errors };
}
